import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from './interfaces';
import { combineLatest, EMPTY, Subject, take } from 'rxjs';

import { Location } from '@angular/common';

import * as orderSelector from './core/store/order/order.selectors';
import * as uiSelector from './core/store/ui/ui.selectors';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { TranslateService } from '@ngx-translate/core';
import { StartOnlineOfflineCheck, SetLanguage, SetIsMobile, SetIsTablette, SetEmployeeUserId } from './core/store/ui';

import { MatIconRegistry } from '@angular/material/icon';

import { UpdateSwService } from './core/services/update-sw.service';
import { AuthService } from './core/services/auth.service';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Title, DomSanitizer } from '@angular/platform-browser';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { projectID } from './core/data/theme-config';
import { PreviousRouteService } from './core/services/previous-route.service';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SetToken } from './core/store/order';
import { UserType } from 'src/app/core/models/user';
import { getUserInfo } from 'src/app/core/store/order/order.selectors';
import {InjectIntercom} from 'src/app/core/injectors/Intercom'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {

  isEmbedded: boolean = false;
  currentUserId: string | undefined = undefined;

  constructor(
    private injectIntercom: InjectIntercom,
    private fb: FormBuilder,
    private store: Store<AppState>,
    public breakpointObserver: BreakpointObserver,
    translate: TranslateService,
    private sw: UpdateSwService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private titleService: Title,
    private router: Router,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private previousRouteService: PreviousRouteService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private location: Location
  ) {
    translate?.setDefaultLang('fr');
    this.store.select(uiSelector.getLanguage)?.subscribe(ln => translate.use(ln));
    this.iconRegistry?.addSvgIcon(
      'notifications',
      this.sanitizer?.bypassSecurityTrustResourceUrl('assets/icons/notifications.svg')
    );
  }

  private destroy$ = new Subject<void>();

  ngOnInit() {

    this.store.dispatch(new StartOnlineOfflineCheck());
    // check the service worker for updates
    this.sw?.checkForUpdates();

    // set the title of the page
    this.router.events
      ?.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.router),
        takeUntil(this.destroy$)
      )
      ?.subscribe(() => {
        const title = this.getTitle(this.router?.routerState, this.router?.routerState?.root)?.join(' | ');
        this.titleService?.setTitle(this.getWebsiteTitle() + (title === '' ? '' : ' | ' + title));
      });

    // check if the user is on a mobile device
    this.breakpointObserver?.observe(['(min-width: 576px)'])?.subscribe((state: BreakpointState) => {
      if (state?.matches) {
        this.store.dispatch(new SetIsMobile(false));
      } else {
        this.store.dispatch(new SetIsMobile(true));
      }
    });

    // check if the user is on a tablet device
    this.breakpointObserver?.observe(['(min-width: 768px)'])?.subscribe((state: BreakpointState) => {
      if (state?.matches) {
        this.store.dispatch(new SetIsTablette(false));
      } else {
        this.store.dispatch(new SetIsTablette(true));
      }
    });

    // NEEDS REFACTORING

    // check if the user is logged in
    this.route?.queryParamMap?.subscribe(params => {
      const jwt = params?.get('jwt') ?? null;
      if (jwt) {
        // clean url
        const currentParamsUrl = new URL(location.href);
        currentParamsUrl.searchParams.delete('jwt')
        const redirectUrlWithParams = currentParamsUrl.search
        this.location.replaceState(redirectUrlWithParams)
      }

      const redirectUrl = params?.get('redirect') ?? null;
      const lan = params?.get('ln') ?? null;
      this.isEmbedded = params?.get('embedded') === 'true';

      if (['fr', 'nl', 'de', 'en'].includes(lan)) {
        this.store.dispatch(new SetLanguage(lan as 'fr' | 'nl' | 'de' | 'en'));
      }

      const employeeUserId = this.getEmployeeUserId({ params, redirectUrl });
      if (employeeUserId) this.store.dispatch(new SetEmployeeUserId(employeeUserId));

      combineLatest(
        this.store.select(orderSelector?.getUserID),
        this.store.select(orderSelector?.getUserEmail),
        (uid, email) => ({
          uid,
          email
        })
      )?.subscribe(({ uid, email }) => {
        if (uid && uid !== '' && this.currentUserId !== uid  && email === '') {
          this.currentUserId = uid;
          // this.authService?.loginWithUserIDAsync(uid)
        }
      });

      // if there is a redirect url
      if (redirectUrl) {
        const currentParamsUrl = new URL(location.href); // temp object to keep current url params
        currentParamsUrl.searchParams.delete('redirect') // guarantee we remove redirect param & keep the others
        const redirectUrlWithParams = redirectUrl + currentParamsUrl.search // reconstruct target URL
        location.href = redirectUrlWithParams // redirection with page reload
      }

      combineLatest([
        this.store.select(getUserInfo),
      ]).pipe(take(1)).subscribe(([userInfo]) => {
        if (!userInfo) return;
        this.injectIntercom.initIntercom({
          app_id: 'h40i50a3',
          email: userInfo.email,
          user_id: userInfo.userid,
          customer_type: (userInfo.userType === UserType.Particulier || userInfo.userType === UserType.OrderRequestor) ? "particulier" : "pro",
          customer_type_details: (({
            [UserType.Particulier]: "Particulier",
            [UserType.Agence]: "Agence",
            [UserType.Certificateur]: "Certificateur",
            [UserType.Notaire]: "Notaire",
            [UserType.Installateur]: "Installateur",
            [UserType.Avocat]: "Avocat",
            [UserType.Syndic]: "Syndic",
            [UserType.Promoteur]: "Promoteur",
            [UserType.Public_AIS]: "Public_AIS",
            [UserType.Multipropriétaire]: "Multipropriétaire",
            [UserType.Banque]: "Banque",
            [UserType.Architecte]: "Architecte",
            [UserType.DesignOffice]: "DesignOffice",
            [UserType.Certifier]: "Certifier",
            [UserType.Installateur_Chauffage]: "Installateur_Chauffage",
            [UserType.General_Contractor]: "General_Contractor",
            [UserType.Surveyor]: "Surveyor",
            [UserType.Expert_Immobilier]: "Expert_Immobilier",
            [UserType.Administration_Communale]: "Administration_Communale",
            [UserType.OrderRequestor]: "OrderRequestor",
            _: "Other"
          })[userInfo.userType] || "Other").toLowerCase(),
          customer_orders: userInfo.ordersCount,
        });
      })
    });

    this.store
      .select(orderSelector.getIsLoggedIn)
      .pipe(
        distinctUntilChanged(),
        switchMap(logged => {
          if (!logged) {
            return EMPTY;
          }
          return this.route.queryParamMap.pipe(
            take(1),
            switchMap(params => {
              if (params.get('jwt')) {
                return EMPTY;
              }
              return combineLatest([
                this.store.select(orderSelector.getToken),
                this.store.select(orderSelector.getTokenValidUntil),
                this.store.select(orderSelector.getRefreshTokenValidUntil)
              ]).pipe(filter(([token]) => !!token), take(1));
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([token, tokenExpirationDate, refreshTokenExpirationDate]) => {
        this.authService.refreshUserInfo(token);
        const tokenExpiration = tokenExpirationDate ? Number(tokenExpirationDate as Date) : null;
        const refreshTokenExpiration = refreshTokenExpirationDate ? Number(refreshTokenExpirationDate as Date) : null;
        if (!tokenExpiration || !refreshTokenExpiration) {
          return;
        }
        const now = Number(new Date());
        if (this.authService.userId && now >= refreshTokenExpiration) {
          this.authService.logout();
        } else if (this.authService.userId && now >= tokenExpiration && now <= refreshTokenExpiration) {
          this.authService.refreshToken();
        }
      });

    // Initialize state of previous route service
    this.previousRouteService?.setPreviousUrl(location?.pathname);
  }

  @HostListener('document:isEmbedded', ['$event'])
  handleIsEmbeddedEvent(event: CustomEvent) {
    this.isEmbedded = event.detail.isEmbedded;
  }

  ngOnDestroy() {
    this.destroy$?.next();
    this.destroy$?.complete();
  }

  ngAfterViewInit(): void {
    const ieIE = this.navigatorIsIE();

    if (ieIE) {
      alert(
        'Our website is not compatible with Internet Explorer, please use any other modern browser (Chrome, Firefox, EDGE, Safari,...)'
      );
    }
  }

  getEmployeeUserId({ params, redirectUrl }: { params: any; redirectUrl: string | null }): string | undefined {
    let employeeUserId: string | undefined;
    try {
      employeeUserId = params?.get('employeeuserid') || new URL(redirectUrl, location.origin).searchParams.get('employeeuserid') || undefined;
    } catch (_error) {
      // If the URL is invalid, we can safely ignore the error and return undefined
      employeeUserId = undefined;
    }
    return employeeUserId;
  }

  getTitle(state, parent) {
    const data = [];
    if (parent?.snapshot?.data?.title) {
      data.push(parent?.snapshot?.data?.title);
    }

    if (state && parent) {
      const child = state?.firstChild(parent);
      if (child) {
        data?.push(...this.getTitle(state, child));
      }
    }
    return data;
  }

  getWebsiteTitle(): string {
    return projectID === 'greenfish'
      ? 'Greenfish'
      : projectID === 'hainaut'
      ? 'MDNH'
      : projectID === 'namur'
      ? 'MDNN'
      : projectID === 'liege'
      ? 'MDNL'
      : projectID === 'nvn'
      ? 'NVN'
      : 'Certinergie';
  }

  navigatorIsIE(): boolean {
    const ua = window?.navigator?.userAgent;
    const msie = ua?.indexOf('MSIE ');

    return msie > 0 || !!navigator?.userAgent?.match(/Trident.*rv\:11\./);
  }
}
